import React, { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { saveRemotePhone, verifyRemotePhone } from './deviceRemoteApi';

export default function RemotePhoneOnboarding({ areaId, areaName, siteName }) {
  const [open, setOpen] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [code, setCode] = useState('');
  const [verifiedCode, setVerifiedCode] = useState('');
  const [deviceName, setDeviceName] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const generation = useRef(0);
  const inFlight = useRef(false);
  const connectUrl = `${window.location.origin}/connect`;

  useEffect(() => {
    generation.current += 1;
    inFlight.current = false;
    setCode(''); setVerifiedCode(''); setDeviceName(''); setBusy(false); setMessage(''); setError('');
    return () => { generation.current += 1; };
  }, [areaId]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    QRCode.toDataURL(connectUrl, { errorCorrectionLevel: 'M', margin: 2, width: 180 })
      .then((value) => { if (!cancelled) setQrDataUrl(value); })
      .catch(() => { if (!cancelled) setQrDataUrl(''); });
    return () => { cancelled = true; };
  }, [connectUrl, open]);

  const submit = async (event) => {
    event.preventDefault();
    if (inFlight.current) return;
    const version = generation.current;
    const saving = Boolean(verifiedCode);
    const trimmedCode = code.trim();
    if (!areaId) { setError('Choose an area before adding a phone remote.'); return; }
    if (!saving && !/^\d{6}$/.test(trimmedCode)) { setError('Enter the six-digit verification code shown on the other phone.'); return; }
    if (saving && !deviceName.trim()) { setError('Please enter device name.'); return; }
    inFlight.current = true; setBusy(true); setMessage(''); setError('');
    try {
      const result = saving
        ? await saveRemotePhone({ verificationCode: verifiedCode, deviceName: deviceName.trim(), areaId })
        : await verifyRemotePhone({ code: trimmedCode, areaId });
      if (generation.current !== version) return;
      if (!result.success) throw new Error(result.message || 'Could not add phone remote.');
      if (saving) {
        setCode(''); setVerifiedCode(''); setDeviceName('');
        setMessage('Phone remote added. Continue on the other phone to open its remote.');
      } else {
        setVerifiedCode(trimmedCode);
        setMessage('Verified — please enter the device name.');
      }
    } catch (failure) {
      if (generation.current === version) setError(failure.message || 'Could not add phone remote.');
    } finally {
      if (generation.current === version) { inFlight.current = false; setBusy(false); }
    }
  };

  return <section className="phone-remote__screen-routines phone-remote__phone-onboarding" aria-label="Add another remote">
    <button type="button" className="phone-remote__action-btn" aria-expanded={open}
      onClick={() => setOpen((value) => !value)}>Add another remote</button>
    {open && <>
      <p>{[siteName, areaName].filter(Boolean).join(' · ') || 'Selected area'}</p>
      <div className="phone-remote__connect-qr">
        {qrDataUrl && <img src={qrDataUrl} alt={`QR code for ${connectUrl}`} width="180" height="180" />}
        <div><p>Scan with the other phone, then enter the verification code shown there.</p>
          <a href={connectUrl} target="_blank" rel="noreferrer">{connectUrl}</a></div>
      </div>
      <form onSubmit={submit}>
        <label htmlFor="remote-phone-verification-code">Verification Code</label>
        <div className="phone-remote__media-link-input">
          <input id="remote-phone-verification-code" type="text" inputMode="numeric" autoComplete="off"
            value={code} disabled={busy || Boolean(verifiedCode)} onChange={(event) => setCode(event.target.value)} />
          {!verifiedCode && <button type="submit" className="phone-remote__action-btn" disabled={busy || !areaId}>
            {busy ? 'Verifying…' : 'Verify'}</button>}
        </div>
        {verifiedCode && <>
          <label htmlFor="remote-phone-device-name">Device name</label>
          <div className="phone-remote__media-link-input">
            <input id="remote-phone-device-name" type="text" autoComplete="off" value={deviceName}
              disabled={busy} onChange={(event) => setDeviceName(event.target.value)} />
            <button type="submit" className="phone-remote__action-btn" disabled={busy}>{busy ? 'Saving…' : 'Save Device'}</button>
          </div>
          <button type="button" className="phone-remote__ghost-btn" disabled={busy}
            onClick={() => { setVerifiedCode(''); setDeviceName(''); setMessage(''); setError(''); }}>Use another code</button>
        </>}
      </form>
      {message && <p role="status">{message}</p>}
      {error && <p role="alert">{error}</p>}
    </>}
  </section>;
}
